/* ==========================================================================
   gregwein-search.js  —  WS-B: wire the storefront search to GregWein /search
   --------------------------------------------------------------------------
   Points the native predictive search bar (header drawer + /search page) at the
   GregWein relevance-first retrieval endpoint (typo-tolerant, intent-aware),
   and lightly re-ranks the /search results grid by that same relevance.

   NATIVE Shopify search (search.js) is left BYTE-IDENTICAL and is used as an
   OUTAGE-ONLY fallback: if GregWein is disabled, times out, errors, or returns
   nothing, we transparently fall through to the native path. Search never breaks.

   Reversion: delete this file + remove the one <script> tag in
   sections/search-drawer.liquid and sections/main-search.liquid. Nothing else
   changes. (WS-B, 2026-07-01)
   ========================================================================== */
(function () {
  'use strict';

  var CFG = Object.assign({
    endpoint: 'https://gregwein.vercel.app/search', // WS-A live; CORS allows apex+www
    enabled: true,          // master flag / kill switch (set window.GREGWEIN_SEARCH={enabled:false})
    dropdownLimit: 8,       // as-you-type suggestions
    pageLimit: 400,         // results-page: fetch the full ranked set (endpoint caps at 400)
    pageSize: 24,           // results-page: render this many per "Load more" batch
    minChars: 2,
    timeoutMs: 2500,        // fall back to native if slower than this
    // Results page: REPLACE the native product grid with GregWein's relevance-ranked
    // results. Native OR-search returns noise ("cabernet sauvignon" -> Pinot/Chardonnay/
    // sparkling); GregWein returns only the relevant, ranked matches. Facet sidebar stays;
    // if a facet/sort is applied we defer to native faceted browse (additive, never
    // zero-results). No-op if GregWein misses -> native grid untouched.
    enhanceResultsPage: true
  }, (window.GREGWEIN_SEARCH || {}));

  if (!CFG.enabled) return; // kill switch -> native search untouched

  /* ---- helpers ---------------------------------------------------------- */
  function esc(s) {
    return String(s == null ? '' : s).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }
  function money(v) {
    var n = Number(v);
    if (!isFinite(n)) return '';
    return '$' + n.toFixed(2);
  }
  function productHandle(url) {
    var m = String(url || '').match(/\/products\/([^/?#]+)/);
    return m ? m[1] : null;
  }

  // Fetch GregWein /search with a hard timeout. Resolves to the JSON payload, or
  // null on ANY failure (network, timeout, non-200, rate-limit, error field, empty).
  // null is the signal to the caller to use the native fallback.
  function gwSearch(query, opts) {
    opts = opts || {};
    var ctrl = new AbortController();
    var timer = setTimeout(function () { ctrl.abort(); }, CFG.timeoutMs);
    // NOTE: do NOT send type=product — the endpoint reads `type` as a wine_type
    // facet (red/white/...), so type=product matches nothing. Omit it for product
    // search; `type=blog` asks the ARTICLE corpus (the Vine blog surfaces use it).
    var url = CFG.endpoint
      + '?q=' + encodeURIComponent(query)
      + (opts.type ? '&type=' + encodeURIComponent(opts.type) : '')
      + (opts.sort ? '&sort=' + encodeURIComponent(opts.sort) : '')
      + (opts.min != null ? '&min=' + encodeURIComponent(opts.min) : '')
      + (opts.max != null ? '&max=' + encodeURIComponent(opts.max) : '')
      + '&limit=' + encodeURIComponent(opts.limit || CFG.dropdownLimit);
    return fetch(url, { signal: ctrl.signal, headers: { 'Accept': 'application/json' } })
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (data) {
        if (!data || data.error || !Array.isArray(data.results) || !data.results.length) return null;
        return data;
      })
      .catch(function () { return null; })
      .finally(function () { clearTimeout(timer); });
  }

  /* ---- 1) predictive dropdown ------------------------------------------- */
  // Render GregWein results into the native results container, reusing the theme's
  // own .search__heading / .horizontal-products / .horizontal-product markup so it
  // is visually identical to native predictive search.
  function dropdownCard(p) {
    var img = p.image
      ? '<img src="' + esc(p.image) + '" alt="' + esc(p.title) + '" loading="lazy" width="120" height="160" />'
      : '';
    var price = p.price != null
      ? '<div class="flex flex-wrap items-end gap-2"><span class="font-medium text-base">' + money(p.price) + '</span>'
        + (p.compare_at && Number(p.compare_at) > Number(p.price)
            ? ' <s class="text-opacity text-sm">' + money(p.compare_at) + '</s>' : '')
        + '</div>'
      : '';
    return ''
      + '<div class="horizontal-product flex items-center gap-4 md:gap-6 w-full">'
      +   '<a class="horizontal-product__media media media--portrait media--contain relative overflow-hidden flex-auto" href="' + esc(p.url) + '" tabindex="-1">' + img + '</a>'
      +   '<div class="horizontal-product__details flex flex-col justify-start gap-1d5">'
      +     '<div class="block leading-none">'
      +       (p.vendor ? '<p class="text-xs text-opacity">' + esc(p.vendor) + '</p>' : '')
      +       '<a href="' + esc(p.url) + '" class="horizontal-product__title reversed-link font-medium text-base !leading-none">' + esc(p.title) + '</a>'
      +     '</div>'
      +     price
      +   '</div>'
      + '</div>';
  }

  function renderDropdown(container, data, terms, searchUrl) {
    var cards = data.results.map(dropdownCard).join('');
    container.innerHTML = ''
      + '<div class="grid gap-3 md:gap-4 w-full">'
      +   '<p class="search__heading block text-xs uppercase leading-tight tracking-widest">Products</p>'
      +   '<div class="horizontal-products grid">' + cards + '</div>'
      + '</div>'
      + '<div class="drawer__sticky z-1 sticky left-0 bottom-0 w-full">'
      +   '<a class="button button--primary icon-with-text w-full" href="' + esc(searchUrl) + '" is="hover-link">'
      +     '<span class="btn-fill" data-fill></span>'
      +     '<span class="btn-text">See all results</span>'
      +   '</a>'
      + '</div>';
  }

  // Patch the native predictive-search element: route as-you-type through GregWein,
  // fall back to the element's own native renderer on any miss.
  function patchPredictive() {
    var Klass = customElements.get('predictive-search');
    if (!Klass || Klass.prototype.__gwPatched) return !!Klass;
    Klass.prototype.__gwPatched = true;

    var nativeRenderSection = Klass.prototype.renderSection;

    Klass.prototype.renderSection = function (url) {
      var self = this;
      var q = (self.getQuery ? self.getQuery() : (self.input && self.input.value || '')).trim();
      if (q.length < CFG.minChars) return nativeRenderSection.call(self, url);

      // The VINE BLOG's search is the ARTICLE surface (its form is
      // is="blog-predictive-search" and submits type=article) — it must never get
      // product suggestions, even if it inherits this patched prototype
      // (Greg, 2026-07-02: products everywhere EXCEPT the blog bar).
      if (((self.getAttribute && self.getAttribute('is')) || '').indexOf('blog') > -1) {
        return nativeRenderSection.call(self, url);
      }

      var sid = self.getAttribute('data-section-id');
      var container = sid && document.getElementById('PredictiveSearchResults-' + sid);
      if (!container) return nativeRenderSection.call(self, url); // no container -> native

      var searchUrl = (window.theme && theme.routes && theme.routes.search_url ? theme.routes.search_url : '/search')
        + '?q=' + encodeURIComponent(q) + '&options%5Bprefix%5D=last';

      self.setAttribute('loading', '');
      gwSearch(q, { limit: CFG.dropdownLimit }).then(function (data) {
        if (!data) { nativeRenderSection.call(self, url); return; } // fallback
        renderDropdown(container, data, q, searchUrl);
        self.removeAttribute('loading');
        self.setAttribute('results', '');
      });
    };
    return true;
  }

  // The element is defined by search.js (also deferred). Poll briefly until it exists.
  function whenPredictiveReady(tries) {
    if (patchPredictive()) return;
    if (tries <= 0) return;
    setTimeout(function () { whenPredictiveReady(tries - 1); }, 120);
  }

  /* ---- 2) /search results page — replace grid with GregWein results ----- */
  // Build one product card matching the theme's own .product-card markup/classes.
  function resultCardHTML(p, styleMod) {
    var packish = /(\d+\s*pk\b|\d+\s*x\s*750|\bcase\b|6x750|12x750)/i.test(p.title || '');
    var media = p.image
      ? '<div class="product-card__media relative h-auto overflow-hidden">'
        +   '<a class="block relative media media--portrait media--contain overflow-hidden" href="' + esc(p.url) + '" aria-label="' + esc(p.title) + '" tabindex="-1">'
        +     '<img src="' + esc(p.image) + '" alt="' + esc(p.title) + '" loading="lazy" class="mx-auto" />'
        +   '</a>'
        + '</div>'
      : '<div class="product-card__media h-auto overflow-hidden"><span class="block aspect-portrait"></span></div>';
    var price = (p.price != null)
      ? '<div class="price flex mx-auto flex-wrap self-end flex-row items-end gap-1d5">'
        + ((p.compare_at && Number(p.compare_at) > Number(p.price)) ? '<s class="opacity-60 !text-lg">' + money(p.compare_at) + '</s> ' : '')
        + '<span class="!text-xl md:!text-2xl">' + money(p.price) + '</span>'
        + '</div>'
      : '';
    return ''
      + '<div class="card ' + (packish ? 'case-deal-sticker ' : '') + 'product-card ' + esc(styleMod) + ' flex flex-col leading-none relative overflow-hidden">'
      +   media
      +   '<div class="product-card__content md:grow !px-1 !py-4 md:!p-4 flex flex-col justify-start text-center">'
      +     (p.vendor ? '<div class="product-card__top w-full"><span class="caption reversed-link uppercase leading-none md:tracking-widest">' + esc(p.vendor) + '</span></div>' : '')
      +     '<div class="product-card__details flex flex-col grow items-baseline gap-2 w-full">'
      +       '<p class="grow !mx-auto"><a class="product-card__title reversed-link !text-xl md:!text-base !font-normal md:leading-tight" href="' + esc(p.url) + '">' + esc(p.title) + '</a></p>'
      +       price
      +     '</div>'
      +   '</div>'
      + '</div>';
  }

  // ARTICLE results page (type=article): replace the native literal-match results with
  // the GregWein article ranking (typo-tolerant + synonyms + semantic — the brain that
  // answers "how long to decant wine" with the decanting article). Article CARDS link
  // to /blogs/ posts; native results stay untouched on any miss.
  function articleCardHTML(a) {
    var img = a.image
      ? '<a class="block relative media media--landscape overflow-hidden" href="' + esc(a.url) + '" tabindex="-1">'
        + '<img src="' + esc(a.image) + '" alt="' + esc(a.title) + '" loading="lazy" class="mx-auto" /></a>'
      : '';
    var summary = a.summary
      ? '<p class="text-sm text-opacity leading-snug">' + esc(String(a.summary).slice(0, 160)) + '</p>' : '';
    return ''
      + '<div class="card article-card flex flex-col leading-none relative overflow-hidden">'
      +   img
      +   '<div class="article-card__content !px-1 !py-4 md:!p-4 flex flex-col gap-2 text-left">'
      +     (a.blog ? '<span class="caption uppercase leading-none md:tracking-widest text-opacity">' + esc(a.blog) + '</span>' : '')
      +     '<a class="reversed-link !text-lg !font-normal leading-tight" href="' + esc(a.url) + '">' + esc(a.title) + '</a>'
      +     summary
      +   '</div>'
      + '</div>';
  }

  function enhanceArticleResults(q) {
    var container = document.getElementById('ProductGridContainer') || document.querySelector('#MainContent');
    if (!container) return;
    gwSearch(q, { limit: 24, type: 'blog' }).then(function (data) {
      if (!data) return;                                  // miss -> native results untouched
      var grid = container.querySelector('motion-list.card-grid');
      if (!grid) {
        var empty = container.querySelector('.empty-state');
        var ml = document.createElement('motion-list');
        ml.className = 'card-grid card-grid--3 mobile:card-grid--2 grid relative';
        if (empty) empty.replaceWith(ml); else container.insertBefore(ml, container.firstChild);
        grid = ml;
      }
      grid.innerHTML = data.results.map(articleCardHTML).join('');
      grid.setAttribute('data-gw-enhanced', 'articles');
      var n = data.results.length;
      document.querySelectorAll('#ProductCount, [role="status"]').forEach(function (el) {
        if (/result/i.test(el.textContent)) el.textContent = n + (n === 1 ? ' result' : ' results');
      });
      document.querySelectorAll('.pagination').forEach(function (el) { el.style.display = 'none'; });
    });
  }

  function enhanceResultsPage() {
    if (!CFG.enhanceResultsPage) return;
    var params = new URLSearchParams(location.search);
    var q = (params.get('q') || '').trim();
    if (!q) return;
    // On type=article the grid holds ARTICLES — never wine cards (that bug put products
    // under the "Articles" heading, Greg 2026-07-02). But native article search has no
    // common sense ("how long to decant wine" -> 0 hits despite a decanting article), so
    // article searches get the SAME smart ranking against the article corpus instead.
    var stype = (params.get('type') || '').toLowerCase();
    if (stype === 'article' || stype === 'articles') return enhanceArticleResults(q);
    if (stype && stype !== 'product') return;   // pages/other native tabs stay native
    // Refinements: GregWein handles PRICE sorts + price-range facets itself (deferring
    // them to native brought the OR-match noise back exactly for high-intent shoppers —
    // the 'barolo sorted by price -> Barbera first' bug, Greg 2026-07-17). Only TRUE
    // facets (vendor/varietal/region sidebar picks) and unknown sorts go native.
    // filter.v.availability=1 is the form's baked-in default, not a shopper choice.
    var SORTS = { 'price-ascending': 'price-asc', 'price-descending': 'price-desc' };
    var gwSort = null, gwMin = null, gwMax = null, hasFacet = false;
    params.forEach(function (v, k) {
      if (k === 'filter.v.availability') return;                 // default in-stock filter
      if (k === 'filter.v.price.gte') { gwMin = v; return; }     // price range -> endpoint min/max
      if (k === 'filter.v.price.lte') { gwMax = v; return; }
      if (k === 'sort_by') {
        if (SORTS[v]) { gwSort = SORTS[v]; return; }             // price sorts stay GregWein
        if (v === 'relevance' || v === '') return;
        hasFacet = true; return;                                 // unknown sort -> native
      }
      if (k.indexOf('filter.') === 0) hasFacet = true;           // true facet -> native browse
    });
    if (hasFacet) return;

    var container = document.getElementById('ProductGridContainer');
    if (!container) return;
    var grid = container.querySelector('motion-list.card-grid');
    // preserve the theme's column class from the live grid; default to 3-up.
    var gridClass = grid ? grid.className : 'card-grid card-grid--3 mobile:card-grid--2 grid relative';
    var styleMod = '';
    var sample = container.querySelector('.product-card');
    if (sample) sample.classList.forEach(function (c) { if (c.indexOf('product-card--') === 0) styleMod = c; });

    gwSearch(q, { limit: CFG.pageLimit, sort: gwSort, min: gwMin, max: gwMax }).then(function (data) {
      if (!data) return; // GregWein miss -> keep native grid exactly as-is
      var results = data.results;
      if (!grid) {
        // native found nothing but GregWein did: replace the empty-state with a grid
        var empty = container.querySelector('.empty-state');
        var ml = document.createElement('motion-list');
        ml.className = gridClass;
        if (empty) empty.replaceWith(ml); else container.insertBefore(ml, container.firstChild);
        grid = ml;
      }
      grid.innerHTML = '';
      grid.setAttribute('data-gw-enhanced', '1');

      // Paginate client-side: render a batch, "Load more" appends the next batch.
      var moreWrap = document.createElement('div');
      moreWrap.className = 'gw-load-more-wrap flex justify-center w-full';
      moreWrap.style.marginTop = '28px';
      grid.insertAdjacentElement('afterend', moreWrap);
      var shown = 0;
      function updateMoreBtn() {
        var remaining = results.length - shown;
        if (remaining > 0) {
          moreWrap.innerHTML = '<button type="button" class="button button--primary gw-load-more"><span class="btn-fill" data-fill></span><span class="btn-text">Load more (' + remaining + ' more)</span></button>';
          moreWrap.querySelector('.gw-load-more').addEventListener('click', renderBatch);
        } else {
          moreWrap.innerHTML = '';
        }
      }
      function renderBatch() {
        var slice = results.slice(shown, shown + CFG.pageSize);
        grid.insertAdjacentHTML('beforeend', slice.map(function (p) { return resultCardHTML(p, styleMod); }).join(''));
        shown += slice.length;
        updateMoreBtn();
      }
      renderBatch();

      // reflect the TOTAL count; hide native pagination (its set no longer matches).
      var n = results.length;
      document.querySelectorAll('#ProductCount, [role="status"]').forEach(function (el) {
        if (/result/i.test(el.textContent)) el.textContent = n + (n === 1 ? ' result' : ' results');
      });
      document.querySelectorAll('.pagination').forEach(function (el) { el.style.display = 'none'; });
    });
  }

  /* ---- boot ------------------------------------------------------------- */
  function boot() {
    whenPredictiveReady(25); // ~3s window for search.js to define the element
    if (document.body.classList.contains('template-search')
        || document.querySelector('#ProductGridContainer')) {
      enhanceResultsPage();
    }
  }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
